import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { CheckCircle2, Circle, Loader2 } from 'lucide-react';
import { useAgentProfile } from '@/hooks/useAgentProfile';
import { useTechStack } from '@/hooks/useTechStack';
import { useUserReviews } from '@/hooks/useUserReviews';
import { useAuth } from '@/contexts/AuthContext';
import { Link } from 'react-router-dom';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useState } from 'react';
import { toast } from 'sonner';

interface SetupStep {
  id: string;
  label: string;
  isComplete: boolean;
  action: () => void;
  actionLabel: string;
}

export function SetupProgressCard() {
  const { user } = useAuth();
  const { profile, updateProfile, isUpdating } = useAgentProfile();
  const { data: techStack } = useTechStack();
  const { data: reviews } = useUserReviews(user?.id);
  const [showStyleSelect, setShowStyleSelect] = useState(false);

  // Calculate completion from existing fields
  const businessComplete = !!(profile?.deals_closed_last_year && profile?.career_stage);
  const styleComplete = !!profile?.communication_style;
  const techStackComplete = (techStack?.length || 0) >= 3;
  const reviewComplete = (reviews?.length || 0) >= 1;
  const goalsComplete = (profile?.goals?.length || 0) > 0;

  const completedCount = [
    businessComplete,
    styleComplete,
    techStackComplete,
    reviewComplete,
    goalsComplete,
  ].filter(Boolean).length;

  const progressPercent = (completedCount / 5) * 100;

  const handleStyleChange = async (value: string) => {
    try {
      await updateProfile({ communication_style: value });
      toast.success('Communication style saved');
      setShowStyleSelect(false);
    } catch (error) {
      toast.error('Failed to save style');
    }
  };

  if (completedCount === 5) {
    return null; // Hide when complete
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Complete Your Circle Setup</CardTitle>
        <CardDescription>
          So I can give you real recommendations tailored to your business
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">
              {completedCount} of 5 complete
            </span>
            <span className="font-medium">{Math.round(progressPercent)}%</span>
          </div>
          <Progress value={progressPercent} className="h-2" />
        </div>

        {/* Steps */}
        <div className="space-y-3">
          {/* Step 1: Business */}
          <div className="flex items-start gap-3">
            {businessComplete ? (
              <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
            ) : (
              <Circle className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
            )}
            <div className="flex-1">
              <p className="text-sm font-medium">Tell us about your business</p>
              {!businessComplete && (
                <Link to="/account/profile">
                  <Button variant="link" size="sm" className="h-auto p-0 text-xs">
                    Complete profile →
                  </Button>
                </Link>
              )}
            </div>
          </div>

          {/* Step 2: Style */}
          <div className="flex items-start gap-3">
            {styleComplete ? (
              <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
            ) : (
              <Circle className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
            )}
            <div className="flex-1">
              <p className="text-sm font-medium">Tell us your style</p>
              {!styleComplete && !showStyleSelect && (
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-xs"
                  onClick={() => setShowStyleSelect(true)}
                >
                  Select your style →
                </Button>
              )}
              {!styleComplete && showStyleSelect && (
                <Select
                  onValueChange={handleStyleChange}
                  disabled={isUpdating}
                >
                  <SelectTrigger className="mt-1 h-8 text-xs">
                    <SelectValue placeholder="How should AskCircle talk to you?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="casual">Chill and casual</SelectItem>
                    <SelectItem value="direct">Direct and to the point</SelectItem>
                    <SelectItem value="detailed">Detailed and thorough</SelectItem>
                  </SelectContent>
                </Select>
              )}
              {isUpdating && (
                <Loader2 className="h-3 w-3 animate-spin inline ml-2" />
              )}
            </div>
          </div>

          {/* Step 3: Tech Stack */}
          <div className="flex items-start gap-3">
            {techStackComplete ? (
              <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
            ) : (
              <Circle className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
            )}
            <div className="flex-1">
              <p className="text-sm font-medium">Tell us what tools you use</p>
              {!techStackComplete && (
                <div className="flex items-center gap-2">
                  <Link to="/account/tech-stack">
                    <Button variant="link" size="sm" className="h-auto p-0 text-xs">
                      Add tools ({techStack?.length || 0}/3 minimum) →
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Step 4: Review */}
          <div className="flex items-start gap-3">
            {reviewComplete ? (
              <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
            ) : (
              <Circle className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
            )}
            <div className="flex-1">
              <p className="text-sm font-medium">Leave one review</p>
              {!reviewComplete && techStackComplete && (
                <p className="text-xs text-muted-foreground">
                  See below to review a tool from your stack
                </p>
              )}
              {!reviewComplete && !techStackComplete && (
                <p className="text-xs text-muted-foreground">
                  Add tools first, then review
                </p>
              )}
            </div>
          </div>

          {/* Step 5: Goals */}
          <div className="flex items-start gap-3">
            {goalsComplete ? (
              <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
            ) : (
              <Circle className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
            )}
            <div className="flex-1">
              <p className="text-sm font-medium">Set your 90-day goals</p>
              {!goalsComplete && (
                <Link to="/account/profile">
                  <Button variant="link" size="sm" className="h-auto p-0 text-xs">
                    Set goals →
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
